import React from 'react';
import { Dialog, DialogContent, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { listAdminNavigation, type AdminNavMatch } from '../../lib/roles';
import { AdminNavSearch } from './AdminNavSearch';
import { isPaletteShortcut } from './adminPaletteShortcut';

const RECENT_KEY = 'tc_admin_recent_pages';
const RECENT_MAX = 5;

/** Recent admin page paths, newest first. Browser storage may be unavailable; then there are simply none. */
function readRecentPaths(): string[] {
    try {
        const raw = window.localStorage.getItem(RECENT_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string').slice(0, RECENT_MAX) : [];
    } catch {
        return [];
    }
}

function rememberRecentPath(path: string): void {
    try {
        const next = [path, ...readRecentPaths().filter((p) => p !== path)].slice(0, RECENT_MAX);
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
        // Not remembering recent pages is fine.
    }
}

/** The admin page for a path, if this user can open it. */
function adminPageFor(permissions: string[], path: string): AdminNavMatch | undefined {
    return listAdminNavigation(permissions).find((m) => m.path === path);
}

interface AdminCommandPaletteProps {
    permissions: string[];
}

/**
 * Cmd/Ctrl+K from any page opens a search over every admin page the user can open, with their recent admin pages
 * shown before anything is typed. Mounted only for users who have admin pages.
 */
export function AdminCommandPalette({ permissions }: AdminCommandPaletteProps) {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const [recent, setRecent] = React.useState<AdminNavMatch[]>([]);
    const navigate = useNavigate();
    const location = useLocation();

    // Remember admin pages as they are visited, however they were reached.
    React.useEffect(() => {
        if (location.pathname.startsWith('/admin') && adminPageFor(permissions, location.pathname)) {
            rememberRecentPath(location.pathname);
        }
    }, [location.pathname, permissions]);

    React.useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (!isPaletteShortcut(event)) return;
            if (!open && document.querySelector('[role="dialog"], [role="alertdialog"]')) {
                return;
            }
            event.preventDefault();
            setOpen((wasOpen) => !wasOpen);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open]);

    React.useEffect(() => {
        if (open) {
            setQuery('');
            setRecent(
                readRecentPaths()
                    .map((path) => adminPageFor(permissions, path))
                    .filter((m): m is AdminNavMatch => m !== undefined),
            );
        }
    }, [open, permissions]);

    const handleSelect = (match: AdminNavMatch) => {
        setOpen(false);
        navigate(match.path);
    };

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { position: 'absolute', top: '12vh', m: 0 } }}
            aria-label="Go to an admin page"
        >
            <DialogContent sx={{ px: 0, py: 1 }}>
                <AdminNavSearch
                    permissions={permissions}
                    query={query}
                    onQueryChange={setQuery}
                    onSelect={handleSelect}
                    emptyItems={recent}
                    emptyTitle="Recent"
                    autoFocus
                />
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block' }}>
                    ↑ ↓ to move · Enter to open · Esc to close
                </Typography>
            </DialogContent>
        </Dialog>
    );
}
